import { styled } from 'styled-system/jsx'

const PickerColumn = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    width: '100%',
  },
})

const TimezoneRow = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
})

const DurationRow = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
})

const DurationLabel = styled('div', {
  base: {
    fontFamily: 'body',
    fontSize: '12px',
    fontWeight: 600,
    color: 'app.fgMuted',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
})

const DurationChipRow = styled('div', {
  base: {
    display: 'flex',
    gap: '6px',
    flexWrap: 'wrap',
  },
})

const DurationChip = styled('button', {
  base: {
    fontFamily: 'body',
    fontSize: '13px',
    fontWeight: 500,
    padding: '6px 14px',
    border: '1px solid',
    borderColor: 'app.border',
    borderRadius: 'pill',
    backgroundColor: 'app.surface',
    color: 'app.fg',
    cursor: 'pointer',
    transition: 'all 160ms',
    _hover: {
      borderColor: 'app.accent',
      color: 'app.accent',
    },
  },
  variants: {
    isSelected: {
      true: {
        backgroundColor: 'app.accent',
        borderColor: 'app.accent',
        color: 'white',
        fontWeight: 600,
        _hover: {
          backgroundColor: 'app.accentHover',
          color: 'white',
        },
      },
    },
  },
})

const TimezoneLabel = styled('label', {
  base: {
    fontFamily: 'body',
    fontSize: '12px',
    fontWeight: 600,
    color: 'app.fgMuted',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
})

const TimezoneSelect = styled('select', {
  base: {
    fontFamily: 'body',
    fontSize: '14px',
    padding: '10px 12px',
    border: '1px solid',
    borderColor: 'app.border',
    borderRadius: 'md',
    backgroundColor: 'app.surface',
    color: 'app.fg',
    cursor: 'pointer',
    _focus: {
      outline: 'none',
      borderColor: 'app.accent',
      boxShadow: '0 0 0 3px rgba(31, 111, 235, 0.15)',
    },
  },
})

const CalendarContainer = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
})

const CalendarHeader = styled('div', {
  base: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
})

const CalendarMonthLabel = styled('h2', {
  base: {
    fontFamily: 'display',
    fontSize: '16px',
    fontWeight: 600,
    margin: 0,
  },
})

const CalendarNavButton = styled('button', {
  base: {
    fontFamily: 'body',
    fontSize: '18px',
    width: '32px',
    height: '32px',
    border: '1px solid',
    borderColor: 'app.border',
    borderRadius: 'md',
    backgroundColor: 'app.surface',
    color: 'app.fg',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    _hover: { backgroundColor: 'book.surfaceMuted' },
    _disabled: { opacity: 0.4, cursor: 'not-allowed' },
  },
})

const WeekdayRow = styled('div', {
  base: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: '4px',
  },
})

const WeekdayCell = styled('div', {
  base: {
    fontFamily: 'body',
    fontSize: '11px',
    fontWeight: 600,
    color: 'app.fgMuted',
    textAlign: 'center',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    paddingY: '4px',
  },
})

const DayGrid = styled('div', {
  base: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: '4px',
  },
})

const DayCell = styled('button', {
  base: {
    fontFamily: 'body',
    fontSize: '14px',
    aspectRatio: '1 / 1',
    border: '1px solid transparent',
    borderRadius: 'md',
    backgroundColor: 'transparent',
    color: 'app.fg',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 160ms',
  },
  variants: {
    state: {
      available: {
        backgroundColor: 'book.primarySoft',
        color: 'book.primary',
        fontWeight: 600,
        _hover: {
          backgroundColor: 'book.primary',
          color: 'white',
        },
      },
      selected: {
        backgroundColor: 'book.primary',
        color: 'white',
        fontWeight: 600,
      },
      disabled: {
        color: 'book.inkMuted',
        cursor: 'not-allowed',
      },
      outsideMonth: {
        color: 'book.inkMuted',
        opacity: 0.4,
        cursor: 'not-allowed',
      },
    },
    isToday: {
      true: {
        outline: '2px solid',
        outlineColor: 'app.accent',
        outlineOffset: '-2px',
      },
    },
  },
})

const SlotsSection = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
})

const SlotsSectionHeader = styled('div', {
  base: {
    fontFamily: 'body',
    fontSize: '14px',
    fontWeight: 600,
    color: 'app.fg',
  },
})

const SlotList = styled('div', {
  base: {
    display: 'grid',
    gridTemplateColumns: { base: '1fr 1fr', md: '1fr 1fr 1fr' },
    gap: '8px',
    maxHeight: '320px',
    overflowY: 'auto',
    paddingRight: '4px',
  },
})

const SlotButton = styled('button', {
  base: {
    fontFamily: 'body',
    fontSize: '14px',
    padding: '12px 8px',
    border: '1px solid',
    borderColor: 'app.border',
    borderRadius: 'md',
    backgroundColor: 'app.surface',
    color: 'app.fg',
    cursor: 'pointer',
    transition: 'all 160ms',
    _hover: {
      borderColor: 'app.accent',
      backgroundColor: 'book.primarySoft',
    },
  },
  variants: {
    isSelected: {
      true: {
        borderColor: 'app.accent',
        backgroundColor: 'app.accent',
        color: 'white',
        fontWeight: 600,
        _hover: {
          backgroundColor: 'app.accentHover',
        },
      },
    },
  },
})

const EmptyState = styled('div', {
  base: {
    fontFamily: 'body',
    fontSize: '13px',
    color: 'app.fgMuted',
    paddingY: '16px',
    textAlign: 'center',
  },
})

const PickerLoading = styled('div', {
  base: {
    fontFamily: 'body',
    fontSize: '13px',
    color: 'app.fgMuted',
    paddingY: '16px',
    textAlign: 'center',
  },
})

const HostTimezoneHint = styled('div', {
  base: {
    fontFamily: 'body',
    fontSize: '11px',
    color: 'app.fgMuted',
    marginTop: '2px',
  },
})

export {
  PickerColumn,
  TimezoneRow,
  TimezoneLabel,
  TimezoneSelect,
  DurationRow,
  DurationLabel,
  DurationChipRow,
  DurationChip,
  CalendarContainer,
  CalendarHeader,
  CalendarMonthLabel,
  CalendarNavButton,
  WeekdayRow,
  WeekdayCell,
  DayGrid,
  DayCell,
  SlotsSection,
  SlotsSectionHeader,
  SlotList,
  SlotButton,
  EmptyState,
  PickerLoading,
  HostTimezoneHint,
}
